<script setup lang="ts">
import type { CoverFeatureItem } from './CoverFeatures.vue';
import MdiFaIcon from './MdiFaIcon.vue';
import { useTooltipManager } from './helpers/use-tooltip-manager';
import { onDeactivated, ref, watch } from 'vue';

type Props = {
  feature: CoverFeatureItem;
  iconSize?: number | string;
};
defineProps<Props>();

const { setActiveTooltip, resetTooltip } = useTooltipManager();

const tooltipId = `feature-bubble-tooltip`;

const isTooltipVisible = ref(false);

onDeactivated(() => {
  isTooltipVisible.value = false;
  resetTooltip();
});

watch(isTooltipVisible, (isVisible: boolean) => {
  if (isVisible) {
    setActiveTooltip(tooltipId);
    return;
  }

  resetTooltip();
});

const featureIconMap: Record<CoverFeatureItem['type'], string> = {
  externalMaterial: 'mdi-clipboard-text-outline',
  teachingMaterial: 'fas fa-list-check',
  noAudio: 'mdi-volume-off',
  bookTalk: 'fas fa-comments',
  languages: 'mdi-earth',
  downloaded: 'mdi-cellphone-check',
  isFullTextBook: 'fas fa-square-poll-horizontal',
  isSpeakingBook: 'fas fa-t',
};

// Get default icon for a feature type
const getDefaultIcon = (feature: CoverFeatureItem): string => {
  return featureIconMap[feature.type] ?? '';
};

const featureTextColorMap: Record<CoverFeatureItem['type'], string> = {
  externalMaterial: 'text-on-warning',
  teachingMaterial: 'text-on-info',
  noAudio: 'text-on-error',
  bookTalk: 'text-on-success',
  languages: 'text-on-secondary',
  downloaded: 'text-on-inverse-surface',
  grade: 'text-on-secondary-container',
  isFullTextBook: 'text-on-secondary-container',
  isSpeakingBook: 'text-inverse-on-surface',
};

// Get text color class for a feature
const getTextColorClass = (feature: CoverFeatureItem): string => {
  if (feature.textColor) {
    return '';
  }
  return featureTextColorMap[feature.type] ?? 'text-on-surface-variant';
};
</script>

<template>
  <v-tooltip
    v-model="isTooltipVisible"
    :disable="!feature.tooltip"
    :location="feature.tooltipLocation ?? 'top'"
    :offset="[16, 0]"
    aria-hidden="true"
  >
    <template #activator="{ props: tooltip }">
      <MdiFaIcon
        v-if="feature.icon || getDefaultIcon(feature)"
        v-bind="tooltip"
        :icon="getDefaultIcon(feature)"
        :size="iconSize"
        :class="getTextColorClass(feature)"
      />
      <span v-bind="tooltip" v-else-if="feature.content" class="book-feature">{{ feature.content }}</span>
    </template>
    <span>{{ feature.tooltip }} </span>
  </v-tooltip>
</template>

<style scoped lang="scss"></style>
